import { useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { toast } from "react-toastify";
import { Authorization } from "../../../Context/Authorization";
import { useEffect } from "react";

const selectStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none", cursor: "pointer" };
const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", outline: "none" };
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const UpdatePresident = () => {
  const [vfcName, setVfcName] = useState([]);
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [members, setMembers] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [previousMembers, setPreviousMembers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { userId, selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  /* Auto-fetch VFCs when selectedJfmcType changes */
  useEffect(() => {
    if (!selectedJfmcType) return;
    const fetchVfc = async () => {
      setLoading(true);
      setSelectedVfc("");
      setMembers([]);
      setPreviousMembers([]);
      setName("");
      setError("");
      try {
        const config = Authorization();
        const response = await axios.post(
          `${Api}/api/vfcs/names`,
          { jfmcTypeId: selectedJfmcType._id },
          config
        );
        const data = response.data.vfcs;
        if (Array.isArray(data)) setVfcName(data);
      } catch (error) {
        console.error("Error fetching VFCs", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVfc();
  }, [selectedJfmcType]);

  const handleVfcChange = async (event) => {
    setLoading(true);
    const vfcId = event.target.value;
    setSelectedVfc(vfcId);
    setName("");
    setError("");

    if (!vfcId) {
      setMembers([]);
      setPreviousMembers([]);
      setLoading(false);
      return;
    }

    try {
      const config = Authorization();

      const membersResponse = await fetch(`${Api}/api/members/getMembersByVFC/${vfcId}`, config);
      if (!membersResponse.ok) throw new Error("Failed to fetch members");
      const membersData = await membersResponse.json();
      setMembers(membersData.members);

      const previousMembersResponse = await fetch(`${Api}/api/member/designation/previous-members`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...config.headers },
        body: JSON.stringify({ vfcId }),
      });
      if (!previousMembersResponse.ok) throw new Error("Failed to fetch previous members");
      const previousMembersData = await previousMembersResponse.json();
      setPreviousMembers(previousMembersData);
    } catch (error) {
      console.error("Error fetching members or previous presidents", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name || !selectedVfc) {
      setError("Please select a member and JFMC");
      return;
    }
    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/member/designation`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...config.headers },
        body: JSON.stringify({
          id: name,
          name: members.find((member) => member._id === name)?.name,
          vfcId: selectedVfc,
          designation: "President",
          date,
          note,
        }),
      });
      const data = await response.json();
      await logActivity(userId, "Updated New President", "Update President Page", Api);
      if (response.ok) {
        toast.success("Designation updated successfully");
        setError("");
        setSelectedVfc("");
        setName("");
        setDate("");
        setNote("");
        setMembers([]);
        setPreviousMembers([]);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error("Error updating designation", error);
      setError("An error occurred while updating the designation");
    }
  };

  /* ── No JFMC Type selected card ── */
  if (!selectedJfmcType) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "48px 24px", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC Type Selected</div>
        <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar to choose a type</div>
      </div>
    );
  }

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

        {/* Left: Form card (2/3) */}
        <div style={{ flex: "0 0 66.666%", maxWidth: "66.666%" }}>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            {/* Card header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
              <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
                <i className="mdi mdi-account-star" />
              </span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Update JFMC President</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Assign a new president to a JFMC</div>
              </div>
            </div>

            <div style={{ padding: "24px 24px" }}>
              {error && (
                <div style={{ background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#C62828", marginBottom: 18 }}>
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 16 }} />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* JFMC Select */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>
                    <i className="mdi mdi-home-group" style={{ color: "#43A047", marginRight: 5 }} />
                    JFMC Name
                  </label>
                  <select
                    value={selectedVfc}
                    onChange={handleVfcChange}
                    style={selectStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                  >
                    <option value="">— Select JFMC —</option>
                    {vfcName.map((vfc) => (
                      <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>
                    ))}
                  </select>
                </div>

                {/* Member Select */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ ...labelStyle, color: members.length === 0 ? "#A0AEC0" : "#2D3748" }}>
                    <i className="mdi mdi-account-outline" style={{ color: members.length === 0 ? "#CBD5E0" : "#43A047", marginRight: 5 }} />
                    Member
                  </label>
                  <select
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={members.length === 0}
                    style={{ ...selectStyle, color: members.length === 0 ? "#A0AEC0" : "#1A202C", background: members.length === 0 ? "#F7FAFC" : "#fff", cursor: members.length === 0 ? "not-allowed" : "pointer" }}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                  >
                    <option value="">— Select Member —</option>
                    {members.map((member) => (
                      <option key={member._id} value={member._id}>{member.name}</option>
                    ))}
                  </select>
                </div>

                {/* Designation (readonly) */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>
                    <i className="mdi mdi-badge-account-outline" style={{ color: "#43A047", marginRight: 5 }} />
                    Designation
                  </label>
                  <input
                    type="text"
                    value="President"
                    readOnly
                    style={{ ...inputStyle, background: "#F7FAFC", color: "#718096", cursor: "not-allowed" }}
                  />
                </div>

                {/* Date */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>
                    <i className="mdi mdi-calendar-outline" style={{ color: "#43A047", marginRight: 5 }} />
                    Date
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                  />
                </div>

                {/* Note */}
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>
                    <i className="mdi mdi-note-text-outline" style={{ color: "#43A047", marginRight: 5 }} />
                    Note
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Enter note (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                  />
                </div>

                {/* Submit */}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="submit"
                    style={{ padding: "9px 22px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 8px rgba(46,125,50,0.3)", display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <i className="mdi mdi-file-check" />
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right: Previous Presidents (1/3) */}
        <div style={{ flex: "0 0 33.333%", maxWidth: "33.333%" }}>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #EDF2F7", background: "#F7FAF7" }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-history" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Previous Presidents</span>
            </div>
            <div style={{ padding: "16px", maxHeight: 400, overflowY: "auto" }}>
              {previousMembers.length > 0 ? (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  {previousMembers.map((member) => (
                    <li key={member._id} style={{ background: "#F7FAF7", borderRadius: 8, padding: "10px 12px", border: "1px solid #E2E8F0" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>{member.memberId?.name}</div>
                      <div style={{ fontSize: 11, color: "#718096", marginTop: 3, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ background: "#E8F5E9", color: "#1B5E20", padding: "1px 8px", borderRadius: "50rem", fontWeight: 600, fontSize: 11, border: "1px solid #A5D6A7" }}>{member.designation}</span>
                        <span>{new Date(member.date).toLocaleDateString("en-IN")}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: "center", padding: "24px 0", color: "#A0AEC0", fontSize: 13 }}>
                  <i className="mdi mdi-account-clock-outline" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
                  No previous presidents found
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UpdatePresident;
